import { createColumnHelper } from "@tanstack/react-table";
import { Table } from "@/components/Table";
import { HeaderTextBlock, TypographyContainer } from "@/components/Typography";
import { useGetContactsTable } from "@/requests/hooks/collect";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { faExternalLink } from "@fortawesome/pro-duotone-svg-icons";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/helpers";
import Tabs, { ITab } from "@/components/Tabs";

interface IProps {
  channel: "EMAIL" | "SMS";
}

const MarketableAudiences = ({ channel }: IProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const tabs: ITab[] = [
    {
      path: "?marketable=MARKETABLE",
      title: t("common.marketableAudiences.tabs.marketable"),
      checkIsActive: () => searchParams.get("marketable") === "MARKETABLE",
    },
    {
      path: "?marketable=NON_MARKETABLE",
      title: t("common.marketableAudiences.tabs.nonMarketable"),
      checkIsActive: () => searchParams.get("marketable") === "NON_MARKETABLE",
    },
  ];

  const columnHelper = createColumnHelper<IContact>();

  const columns = [
    columnHelper.accessor("identifier", {
      header: t(
        `common.marketableAudiences.table.columns.identifier.${channel}`,
      ),
    }),
    columnHelper.accessor("name", {
      header: t("common.marketableAudiences.table.columns.name"),
      cell: info =>
        !!info?.getValue()
          ? info?.getValue()
          : t("common.marketableAudiences.table.row.unknown"),
    }),
    columnHelper.accessor("marketable", {
      header: t("common.marketableAudiences.table.columns.status.header"),
      cell: info => {
        const isMarketable = info.getValue();

        return (
          <Badge variant={isMarketable ? "success" : "error"}>
            {t(
              `common.marketableAudiences.table.columns.status.${isMarketable}`,
            )}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("status", {
      header: t("common.marketableAudiences.table.columns.rationale"),
      cell: info => t(`common.contacts.rationale.${info.getValue()}`),
    }),
    columnHelper.accessor("lastModifiedAt", {
      header: t("common.marketableAudiences.table.columns.lastModifiedAt"),
      cell: info => formatDate(info.getValue()),
    }),
  ];

  return (
    <>
      <TypographyContainer>
        <HeaderTextBlock
          align="left"
          featureText={t("common.marketableAudiences.header.feature")}
          headingText={t("common.marketableAudiences.header.heading")}
          descriptionText={t(
            `common.marketableAudiences.header.description.${channel}`,
          )}
        />
      </TypographyContainer>

      <div className="my-40">
        <Button
          as="a"
          href="https://www.klaviyo.com/lists"
          icon={faExternalLink}
          iconPosition="end"
          iconClassName="h-15"
          testId="klaviyo-cta">
          {t("common.marketableAudiences.cta")}
        </Button>
      </div>

      <div className="my-40">
        <Tabs tabs={tabs} />
      </div>

      <Table
        columns={columns}
        queryKey={["collect", "marketableAudiences", channel]}
        queryHook={useGetContactsTable}
        filters={{
          channel: channel.toUpperCase(),
          marketable: searchParams.get("marketable") || "MARKETABLE",
        }}
      />
    </>
  );
};

export default MarketableAudiences;
